<div mat-dialog-content class="map-designer-shell">
    <app-wizard-surface
        [showStepper]="false"
        [maxWidthClass]="'max-w-none'"
        [contentClass]="'map-designer-wizard'">
        <div class="map-designer-layout">
            <section class="map-designer-stage" aria-label="Map editor canvas">
                <div id="map" class="map-container"></div>
            </section>

            <aside class="map-designer-panel" aria-label="Map editor settings">
                <div class="map-designer-panel__scroll">
                    <app-form-section-card
                        class="map-designer-inspector-card"
                        [compact]="true"
                        eyebrow="Map setup"
                        [description]="data.name || 'Selected map layer'"
                        [headerInline]="true">
                        <div section-actions class="map-designer-layer-summary" aria-label="Layer summary">
                            <span title="Editable layers">
                                <fa-icon [icon]="['fal', 'pen-ruler']"></fa-icon>
                                {{ drawnLayerCount }}
                            </span>
                            <span title="Reference layers">
                                <fa-icon [icon]="['fal', 'layer-group']"></fa-icon>
                                {{ referenceLayerCount }}
                            </span>
                        </div>

                        <section class="map-designer-location" aria-labelledby="map-location-title">
                            <div class="map-designer-section-heading">
                                <span class="map-designer-section-heading__icon" aria-hidden="true">
                                    <fa-icon [icon]="['fal', 'location-dot']"></fa-icon>
                                </span>
                                <div>
                                    <h2 id="map-location-title">Location</h2>
                                    <p>Map center coordinates</p>
                                </div>
                            </div>
                            <app-coordinate-editor
                                class="map-designer-coordinate-editor"
                                [compact]="true"
                                (valueChange)="coordinateChanged($event)"></app-coordinate-editor>
                        </section>

                        <nav
                            class="map-designer-tabs"
                            role="tablist"
                            aria-label="Overlay settings"
                            (keydown)="onInspectorTabKeydown($event)">
                            <button
                                type="button"
                                role="tab"
                                id="map-designer-tab-image"
                                aria-controls="map-designer-panel-image"
                                [attr.aria-selected]="activeInspectorTab === 'image'"
                                [attr.tabindex]="activeInspectorTab === 'image' ? 0 : -1"
                                [class.map-designer-tab--active]="activeInspectorTab === 'image'"
                                (click)="setInspectorTab('image')">
                                <fa-icon [icon]="['fal', 'image']"></fa-icon>
                                <span>Image</span>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                id="map-designer-tab-transform"
                                aria-controls="map-designer-panel-transform"
                                [attr.aria-selected]="activeInspectorTab === 'transform'"
                                [attr.tabindex]="activeInspectorTab === 'transform' ? 0 : -1"
                                [class.map-designer-tab--active]="activeInspectorTab === 'transform'"
                                (click)="setInspectorTab('transform')">
                                <fa-icon [icon]="['fal', 'arrows-up-down-left-right']"></fa-icon>
                                <span>Transform</span>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                id="map-designer-tab-layers"
                                aria-controls="map-designer-panel-layers"
                                [attr.aria-selected]="activeInspectorTab === 'layers'"
                                [attr.tabindex]="activeInspectorTab === 'layers' ? 0 : -1"
                                [class.map-designer-tab--active]="activeInspectorTab === 'layers'"
                                (click)="setInspectorTab('layers')">
                                <fa-icon [icon]="['fal', 'layer-group']"></fa-icon>
                                <span>Layers</span>
                            </button>
                        </nav>

                        <div class="map-designer-workspace">
                            @if (activeInspectorTab === 'image') {
                                <section
                                    id="map-designer-panel-image"
                                    role="tabpanel"
                                    aria-labelledby="map-designer-tab-image"
                                    class="map-designer-tab-panel">
                                    <div class="map-designer-section-heading">
                                        <span class="map-designer-section-heading__icon map-designer-section-heading__icon--image" aria-hidden="true">
                                            <fa-icon [icon]="['fal', 'image']"></fa-icon>
                                        </span>
                                        <div>
                                            <h2>Overlay image</h2>
                                            <p>Original floor plan</p>
                                        </div>
                                        <span
                                            class="map-designer-status"
                                            [class.map-designer-status--ready]="overlayConfig">
                                            {{ overlayConfig ? 'Ready' : 'Empty' }}
                                        </span>
                                    </div>

                                    <app-image-uploader
                                        mode="direct"
                                        [imageId]="overlayUploaderImageId"
                                        [imageUrl]="overlayImageUrl"
                                        (imageUploaded)="onOverlayImageUploaded($event)"
                                        (imageRemoved)="onOverlayImageRemoved()"></app-image-uploader>
                                </section>
                            }

                            @if (activeInspectorTab === 'transform') {
                                <section
                                    id="map-designer-panel-transform"
                                    role="tabpanel"
                                    aria-labelledby="map-designer-tab-transform"
                                    class="map-designer-tab-panel">
                                    <div class="map-designer-section-heading">
                                        <span class="map-designer-section-heading__icon map-designer-section-heading__icon--transform" aria-hidden="true">
                                            <fa-icon [icon]="['fal', 'arrows-up-down-left-right']"></fa-icon>
                                        </span>
                                        <div>
                                            <h2>Transform</h2>
                                            <p>Aspect-locked placement</p>
                                        </div>
                                        <span class="map-designer-status map-designer-status--locked">
                                            <fa-icon [icon]="['fal', 'lock']"></fa-icon>
                                            Locked
                                        </span>
                                    </div>

                                    <label class="overlay-opacity" [class.overlay-opacity--disabled]="!overlayConfig">
                                        <span>Opacity</span>
                                        <strong>{{ overlayOpacity | percent:'1.0-0' }}</strong>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            aria-label="Overlay opacity"
                                            [disabled]="!overlayConfig"
                                            [value]="overlayOpacity"
                                            (input)="overlayOpacityChanged($any($event.target).value)">
                                    </label>

                                    <div class="map-designer-actions">
                                        <button
                                            type="button"
                                            app-wizard-action-button="secondary"
                                            size="small"
                                            [disabled]="!overlayConfig"
                                            (click)="fitOverlayToGeoJson()">
                                            <fa-icon [icon]="['fal', 'expand']"></fa-icon>
                                            <span>Fit to drawing</span>
                                        </button>
                                        <button
                                            type="button"
                                            app-wizard-action-button="danger"
                                            size="small"
                                            [disabled]="!overlayConfig"
                                            (click)="removeOverlayImage()">
                                            <fa-icon [icon]="['fal', 'trash-alt']"></fa-icon>
                                            <span>Remove</span>
                                        </button>
                                    </div>

                                    <dl class="map-designer-stats">
                                        <div>
                                            <dt>Aspect ratio</dt>
                                            <dd>{{ overlayAspectRatioLabel }}</dd>
                                        </div>
                                        <div>
                                            <dt>Controls</dt>
                                            <dd>{{ overlayConfig ? 'Move / Size / Rotate' : '-' }}</dd>
                                        </div>
                                    </dl>
                                </section>
                            }

                            @if (activeInspectorTab === 'layers') {
                                <section
                                    id="map-designer-panel-layers"
                                    role="tabpanel"
                                    aria-labelledby="map-designer-tab-layers"
                                    class="map-designer-tab-panel">
                                    <div class="map-designer-section-heading">
                                        <span class="map-designer-section-heading__icon map-designer-section-heading__icon--layers" aria-hidden="true">
                                            <fa-icon [icon]="['fal', 'layer-group']"></fa-icon>
                                        </span>
                                        <div>
                                            <h2>Drawing layers</h2>
                                            <p>Current editor content</p>
                                        </div>
                                    </div>

                                    <div class="map-designer-layer-list">
                                        <div>
                                            <span class="map-designer-layer-list__icon map-designer-layer-list__icon--editable">
                                                <fa-icon [icon]="['fal', 'pen-ruler']"></fa-icon>
                                            </span>
                                            <span>
                                                <strong>Editable</strong>
                                                <small>Saved with this map</small>
                                            </span>
                                            <b>{{ drawnLayerCount }}</b>
                                        </div>
                                        <div>
                                            <span class="map-designer-layer-list__icon map-designer-layer-list__icon--reference">
                                                <fa-icon [icon]="['fal', 'eye']"></fa-icon>
                                            </span>
                                            <span>
                                                <strong>Reference</strong>
                                                <small>Visible alignment guides</small>
                                            </span>
                                            <b>{{ referenceLayerCount }}</b>
                                        </div>
                                    </div>
                                </section>
                            }
                        </div>
                    </app-form-section-card>
                </div>

                <footer class="map-designer-panel__footer">
                    <button
                        type="button"
                        app-wizard-action-button="secondary"
                        mat-dialog-close>
                        <fa-icon [icon]="['fal', 'xmark']"></fa-icon>
                        <span>Cancel</span>
                    </button>
                    <button
                        type="button"
                        app-wizard-action-button="success"
                        (click)="onSave()">
                        <fa-icon [icon]="['fal', 'check']"></fa-icon>
                        <span>Save map</span>
                    </button>
                </footer>
            </aside>
        </div>
    </app-wizard-surface>
</div>
